import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Book, LibraryDownload } from '../api/types';
import { formatDate, languageNameIn, traditionLabel, useJourneyLocale } from '../i18n/journey';
import { useUser } from '../state/UserContext';
import { ALL_TRADITIONS, type Tradition } from '../theme/tokens';

export function Library() {
  const { me } = useUser();
  const { t, lang } = useJourneyLocale();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Tradition | 'all'>('all');
  const [attempt, setAttempt] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Per-book signed link / failure. A real link the user clicks, rather than an async
  // window.open that popup blockers commonly swallow while the UI assumes it worked.
  const [links, setLinks] = useState<Record<string, LibraryDownload>>({});
  const [linkErrors, setLinkErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getLibrary(filter === 'all' ? undefined : filter)
      .then((res) => {
        if (!cancelled) setBooks(res.books);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('library.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `t` is read at failure time only; a language change must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, attempt]);

  const handleGetLink = async (bookId: string) => {
    setDownloadingId(bookId);
    setLinkErrors((prev) => {
      const next = { ...prev };
      delete next[bookId];
      return next;
    });
    try {
      const res = await api.getLibraryDownload(bookId);
      setLinks((prev) => ({ ...prev, [bookId]: res }));
    } catch (e) {
      setLinkErrors((prev) => ({ ...prev, [bookId]: e instanceof Error ? e.message : t('library.linkFailed') }));
    } finally {
      setDownloadingId(null);
    }
  };

  const filterOptions: Array<Tradition | 'all'> = ['all', ...(me?.traditions.length ? me.traditions : ALL_TRADITIONS)];

  return (
    <div>
      <h1 className="page-title">{t('library.title')}</h1>
      <p className="page-subtitle">{t('library.subtitle')}</p>

      <div className="filter-row">
        {filterOptions.map((f) => (
          <button
            key={f}
            type="button"
            className={`filter-chip${filter === f ? ' selected' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('library.all') : traditionLabel(t, f)}
          </button>
        ))}
      </div>

      {error && (
        <p className="error-text" role="alert">
          {error}{' '}
          <button type="button" className="btn-inline-link" onClick={() => setAttempt((a) => a + 1)}>
            {t('shared.tryAgain')}
          </button>
        </p>
      )}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : books.length === 0 ? (
        !error && <div className="empty-state">{t('library.empty')}</div>
      ) : (
        <div className="library-list">
          {books.map((b) => {
            const link = links[b.bookId];
            const linkValid = link && Date.parse(link.expiresAt) > Date.now();
            const linkError = linkErrors[b.bookId];
            const language = b.language ?? 'en';
            return (
              <div key={b.bookId} className="library-item">
                <div>
                  <div className="library-item-title">{b.title}</div>
                  <div className="library-item-meta">
                    {traditionLabel(t, b.tradition)} &middot;{' '}
                    {t('library.edition', { language: languageNameIn(lang, language) })}
                    {' '}&middot; {b.license === 'public-domain' ? t('library.publicDomain') : b.license}
                    {b.sourceName && (
                      <>
                        {' '}&middot;{' '}
                        {b.sourceUrl ? (
                          <a href={b.sourceUrl} target="_blank" rel="noopener noreferrer">
                            {b.sourceName}
                          </a>
                        ) : (
                          b.sourceName
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="library-item-actions">
                  {linkValid ? (
                    <>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary library-download-link"
                      >
                        {t('library.openText')} &#8599;
                      </a>
                      <span className="library-item-note">
                        {t('library.linkUntil', {
                          time: formatDate(lang, new Date(link.expiresAt), { hour: 'numeric', minute: '2-digit' }),
                        })}
                      </span>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleGetLink(b.bookId)}
                      disabled={downloadingId === b.bookId}
                    >
                      {downloadingId === b.bookId
                        ? t('library.gettingLink')
                        : linkError
                          ? t('shared.tryAgain')
                          : t('library.getDownload')}
                    </button>
                  )}
                  {linkError && (
                    <span className="library-item-note" role="alert">
                      {linkError}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
